import React from 'react';
import { EquityContent, RelevanceContent } from '../../components/charts/ChartLayout';
import type { AuthoredNarrativeBundle } from './authoredNarrativeRender';
import { resolveEquityV6Compact, type EquityV6Compact } from './equityV2Resolver';
import { resolveRelevanceV2, type RelevanceV2Compact } from './relevanceV2Resolver';

type AuthoredBundleSlice = Pick<
  AuthoredNarrativeBundle,
  'relevance' | 'relevanceV2' | 'narrativeChartId' | 'narrativeViewId'
>;

function renderRelevanceV2Compact(title: string, compact: RelevanceV2Compact) {
  return (
    <RelevanceContent title={title} description={compact.intro}>
      {compact.sections.map((section, index) => (
        <div className="methodology-details-simple" key={`${section.title}-${index}`}>
          <h5>{section.title}</h5>
          <p>{section.body}</p>
        </div>
      ))}
      {compact.note ? (
        <p className="relevance-v2-authored-note" style={{ marginTop: 12, color: '#475569', fontSize: 13 }}>
          {compact.note}
        </p>
      ) : null}
    </RelevanceContent>
  );
}

/** Renders exact-authored Relevance V2 compact copy (lead + section cards). */
export function AuthoredRelevancePanel(props: {
  title?: string;
  body?: string;
  bundle?: AuthoredBundleSlice;
  chartId?: string;
  viewId?: string;
}) {
  const { title = 'Relevance', body: bodyProp, bundle, chartId: chartIdProp, viewId: viewIdProp } = props;

  const chartId = bundle?.narrativeChartId ?? chartIdProp;
  const viewId = bundle?.narrativeViewId ?? viewIdProp ?? 'default';

  const compact =
    bundle?.relevanceV2 ??
    (chartId ? resolveRelevanceV2(chartId, viewId) : null);

  if (compact) {
    return renderRelevanceV2Compact(title, compact);
  }

  const body = bodyProp ?? bundle?.relevance ?? '';
  if (!body?.trim()) return null;
  return <RelevanceContent title={title} description={body} />;
}

function renderEquityV6Compact(title: string, compact: EquityV6Compact) {
  return (
    <EquityContent title={title} description={compact.intro}>
      {compact.sections.map((section, index) => (
        <div className="methodology-details-simple" key={`${section.title}-${index}`}>
          <h5>{section.title}</h5>
          <p>{section.body}</p>
        </div>
      ))}
      {compact.note ? (
        <p className="equity-v6-authored-note" style={{ marginTop: 12, color: '#475569', fontSize: 13 }}>
          Data note: {compact.note}
        </p>
      ) : null}
    </EquityContent>
  );
}

type AuthoredEquityBundleSlice = Pick<
  AuthoredNarrativeBundle,
  'equity' | 'equityV6' | 'narrativeChartId' | 'narrativeViewId'
>;

/** Renders exact-authored Equity V6 compact copy (intro + section cards + data note). */
export function AuthoredEquityPanel(props: {
  title?: string;
  body?: string;
  bundle?: AuthoredEquityBundleSlice;
  chartId?: string;
  viewId?: string;
}) {
  const { title = 'Equity', body: bodyProp, bundle, chartId: chartIdProp, viewId: viewIdProp } = props;

  const chartId = bundle?.narrativeChartId ?? chartIdProp;
  const viewId = bundle?.narrativeViewId ?? viewIdProp ?? 'default';

  const compact = bundle?.equityV6 ?? (chartId ? resolveEquityV6Compact(chartId, viewId) : null);
  if (compact) {
    return renderEquityV6Compact(title, compact);
  }

  const body = bodyProp ?? bundle?.equity ?? '';
  if (!body?.trim()) return null;
  return <EquityContent title={title} description={body} />;
}
